// This is a new file for Course Details

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SuccessStories from '../components/SuccessStories';
import MentorSection from '../components/MentorSection';
import CertificateModal from '../components/CertificateModal';
import { 
  ChevronDown, 
  ChevronUp, 
  PlayCircle, 
  Play,
  Star, 
  Users, 
  Clock, 
  Calendar, 
  Award, 
  BookOpen, 
  Download, 
  Share2, 
  Heart,
  CheckCircle,
  AlertCircle,
  Globe,
  Monitor,
  Smartphone,
  MessageCircle,
  TrendingUp,
  Target,
  Code,
  Zap,
  Shield,
  Trophy,
  User,
  Mail,
  Phone,
  Linkedin,
  Building2,
  Briefcase,
  X,
  MapPin,
  Video,
  Headphones,
  Database,
  Server,
  Cloud,
  Brain,
  Bot,
  Cpu,
  Network,
  Eye,
  FileText,
  Layers,
  Palette,
  BarChart3,
  GitBranch,
  TestTube,
  Settings,
  Coffee,
  Pilcrow,
  FileSpreadsheet,
  Gift,
  ArrowRight,
  MessageSquare,
  FileText as FileTextIcon,
  Brain as BrainIcon,
  Users as UsersIcon
} from 'lucide-react';
import { allCourses } from '../data/courses';
import sirImage from '../assets/sir.jfif';
import rishiVyasImage from '../assets/Rishi_Vyas_DataAnalysis.jpg';
import CourseCard from '../components/CourseCard';

// Helper to find course by slug or ID from any category
const findCourseBySlug = (slugOrId) => {
  if (!slugOrId) return null;
  for (const category in allCourses) {
    const course = allCourses[category].find(c => 
      (c.slug && c.slug === slugOrId) || c.id.toString() === slugOrId.toString()
    );
    if (course) {
      return course;
    }
  }
  return null;
};

// Helper to extract YouTube video ID
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};



// Video Modal Component
const VideoModal = ({ videoUrl, isOpen, onClose }) => {
  const videoId = getYouTubeVideoId(videoUrl);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4 bg-black/80">
      <div className="relative w-full max-w-4xl mx-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
        >
          <X className="w-8 h-8" />
        </button>
        
        {/* Video container with 16:9 aspect ratio */}
        <div className="relative pt-[56.25%] w-full bg-black rounded-xl overflow-hidden">
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&controls=1&autoplay=0&mute=0`}
            title="Course Preview"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
            allowFullScreen
            onError={(e) => {
              console.error('YouTube iframe error:', e);
            }}
          ></iframe>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children, defaultOpen = false, icon: Icon }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border-b border-gray-200 dark:border-gray-700 py-6">
            <button
                onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left text-xl font-semibold text-gray-800 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
            >
        <div className="flex items-center">
          {Icon && <Icon className="w-6 h-6 mr-3 text-primary-600" />}
                <span>{title}</span>
        </div>
                {isOpen ? <ChevronUp className="w-5 h-5 text-primary-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                    >
            <div className="mt-6 text-gray-700 dark:text-gray-300 space-y-4">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const StatCard = ({ icon: Icon, label, value, color = "text-primary-600" }) => (
  <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-soft border border-gray-100 dark:border-gray-700 text-center">
    <Icon className={`w-8 h-8 mx-auto mb-2 ${color}`} />
    <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{value}</div>
    <div className="text-sm text-gray-600 dark:text-gray-400">{label}</div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-soft border border-gray-100 dark:border-gray-700 hover:shadow-soft-lg transition-all duration-300">
    <Icon className="w-8 h-8 text-primary-600 mb-4" />
    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
    <p className="text-gray-600 dark:text-gray-400 text-sm">{description}</p>
  </div>
);

const ReviewCard = ({ name, rating, review, date, avatar }) => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-soft border border-gray-100 dark:border-gray-700">
    <div className="flex items-center mb-4">
      <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-4">
        <User className="w-6 h-6 text-primary-600" />
      </div>
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-white">{name}</h4>
        <div className="flex items-center">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
            />
          ))}
          <span className="ml-2 text-sm text-gray-500">{date}</span>
        </div>
      </div>
    </div>
    <p className="text-gray-600 dark:text-gray-400">{review}</p>
  </div>
);

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-gray-200 dark:border-gray-700 py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left font-semibold text-gray-800 dark:text-gray-200 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200 focus:outline-none"
      >
        <span>{question}</span>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="mt-3 text-gray-600 dark:text-gray-400">{answer}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const CourseDetail = () => {
    const { courseSlug } = useParams();
    const navigate = useNavigate();
    const [course, setCourse] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const [thumbnail, setThumbnail] = useState(null);
    const [openCurriculum, setOpenCurriculum] = useState([]);
    const [videoModal, setVideoModal] = useState({ isOpen: false, videoUrl: '', studentName: '', company: '' });
    const [showAllReviews, setShowAllReviews] = useState(false);
    const [showAllVideos, setShowAllVideos] = useState(false);
    const [certificateModal, setCertificateModal] = useState({ isOpen: false });

    // Function to get YouTube thumbnail URL
    const getYouTubeThumbnail = useCallback((videoUrl) => {
        if (videoUrl === "#") return null;
        const videoId = videoUrl.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
        return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : null;
    }, []);

    // Video testimonials data
    const videoTestimonials = useMemo(() => [
        {
            name: "Trupti Nagar Jain",
            company: "Microsoft, USA",
            videoUrl: "https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK",
            thumbnail: getYouTubeThumbnail("https://youtu.be/ZmID8vdF6c8?si=UU_e1K2dKWGfMAsK")
        },
        {
            name: "Sachin Tomar",
            company: "City Bank, Pune",
            videoUrl: "https://www.youtube.com/watch?v=EZROGHX2TCY",
            thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=EZROGHX2TCY")
        },
        {
            name: "Anshul Gupta",
            company: "Wipro, Mumbai",
            videoUrl: "https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s",
            thumbnail: getYouTubeThumbnail("https://www.youtube.com/watch?v=pEvczGfvs9k&t=1s")
        },
        {
            name: "Neeraj Sharma",
            company: "HCL, Netherland",
            videoUrl: "https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT",
            thumbnail: getYouTubeThumbnail("https://youtu.be/q0i7v0ZjmBA?si=_xbZ592JX8kZcZvT")
        },
        {
            name: "Shubham Jain",
            company: "NTT Data, USA",
            videoUrl: "https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul",
            thumbnail: getYouTubeThumbnail("https://youtu.be/OYIiAdhd-Ec?si=0npSzWhmBihZRjul")
        },
        {
            name: "Rakshit Bhatia",
            company: "JD Software, Banglore",
            videoUrl: "https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx",
            thumbnail: getYouTubeThumbnail("https://youtu.be/TAHnPRA-ghA?si=3Tndt4e2WxkyIAbx")
        },
        {
            name: "Shiva Jain",
            company: "TCS, Indore",
            videoUrl: "https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA",
            thumbnail: getYouTubeThumbnail("https://youtu.be/CiXi9FTXnnA?si=fJhDAeqCfTJmAuCA")
        }
    ], [getYouTubeThumbnail]);

    // Video modal functions
    const openVideoModal = useCallback((testimonial) => {
        if (testimonial.videoUrl !== "#") {
            // Convert YouTube URL to embeddable format
            const videoId = testimonial.videoUrl.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/embed\/)([^&\n?#]+)/)?.[1];
            const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&controls=1&autoplay=0&mute=0` : testimonial.videoUrl;
            
            setVideoModal({
                isOpen: true,
                videoUrl: embedUrl,
                studentName: testimonial.name,
                company: testimonial.company
            });
        }
    }, []);

    const closeVideoModal = useCallback(() => {
        setVideoModal({ isOpen: false, videoUrl: '', studentName: '', company: '' });
    }, []);

    // Certificate modal functions
    const openCertificateModal = useCallback(() => {
        setCertificateModal({ isOpen: true });
    }, []);

    const closeCertificateModal = useCallback(() => {
        setCertificateModal({ isOpen: false });
    }, []);

    useEffect(() => {
        console.log('Loading course with slug:', courseSlug);
        const foundCourse = findCourseBySlug(courseSlug);
        setCourse(foundCourse);
        
        if (foundCourse?.videoUrl) {
            setThumbnail(getYouTubeThumbnail(foundCourse.videoUrl));
        }
        // Initialize curriculum open state: first open, rest closed
        if (foundCourse?.curriculum) {
            setOpenCurriculum([true, ...Array(foundCourse.curriculum.length - 1).fill(false)]);
        }
        window.scrollTo(0, 0);
    }, [courseSlug]);

    const handleVideoClick = () => {
        if (course?.videoUrl) {
            setIsVideoOpen(true);
        }
    };

    const handleEnrollNow = () => {
        // Check if course has external URL for redirect
        if (course?.externalUrl) {
            
            // Show loading message
            const loadingMessage = document.createElement('div');
            loadingMessage.innerHTML = `
                <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 9999; display: flex; align-items: center; justify-content: center;">
                    <div style="background: white; padding: 20px; border-radius: 10px; text-align: center;">
                        <div style="margin-bottom: 10px;">🔄 Redirecting to external course platform...</div>
                        <div style="font-size: 12px; color: #666;">If the page doesn't load, please try again later</div>
                    </div>
                </div>
            `;
            document.body.appendChild(loadingMessage);
            
            // Try to open external URL
            try {
                const newWindow = window.open(course.externalUrl, '_blank');
                
                // Remove loading message after 3 seconds
                setTimeout(() => {
                    document.body.removeChild(loadingMessage);
                }, 3000);
                
                // If window.open fails, show fallback
                if (!newWindow) {
                    alert('Please allow popups for this site to access the external course platform.');
                }
                
                // Add fallback option after 5 seconds if main URL fails
                setTimeout(() => {
                    if (course.fallbackUrl && window.confirm('The course platform seems to be slow or experiencing issues. Would you like to try the main course website instead?')) {
                        window.open(course.fallbackUrl, '_blank');
                    }
                }, 5000);
                
                // Additional timeout for 504 errors
                setTimeout(() => {
                    if (course.fallbackUrl && window.confirm('The course platform appears to be down (504 Gateway Time-out). Would you like to try the main course website?')) {
                        window.open(course.fallbackUrl, '_blank');
                    }
                }, 8000);
                
            } catch (error) {
                document.body.removeChild(loadingMessage);
                
                // Check if it's a 504 Gateway Time-out error
                if (error.message && error.message.includes('504')) {
                    alert('The course platform is currently experiencing high traffic or maintenance. Please try again in a few minutes.');
                }
                
                // Try fallback URL if available
                if (course.fallbackUrl) {
                    if (window.confirm('Unable to open the specific course page. Would you like to try the main course website instead?')) {
                        window.open(course.fallbackUrl, '_blank');
                    }
                } else {
                    alert('Unable to open external course platform. Please try again later or contact support.');
                }
            }
        } else {
            // Navigate to enquiry page with course details
            navigate('/enquire', { 
                state: { 
                    courseId: course?.id,
                    courseTitle: course?.title,
                    coursePrice: course?.price,
                    courseCategory: course?.category
                }
            });
        }
    };
    
    if (!course) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading course details...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BookOpen },
    { id: 'curriculum', label: 'Curriculum', icon: Code },
    { id: 'instructor', label: 'Instructor', icon: User },
    { id: 'reviews', label: 'Reviews', icon: Star },
    { id: 'faq', label: 'FAQ', icon: MessageCircle },
  ];

  // Function to get category-specific features
  const getCategoryFeatures = (courseCategory) => {
    const categoryFeatures = {
      'live': [
        { icon: Video, title: 'Live Interactive Classes', description: 'Real-time learning with expert instructors and peer interaction' },
        { icon: Users, title: 'Direct Mentor Access', description: 'Get personalized guidance and doubt resolution from senior mentors' },
        { icon: Clock, title: 'Scheduled Learning', description: 'Structured learning with fixed timings and regular sessions' },
        { icon: Award, title: 'Certification', description: 'Industry-recognized certificate with live project completion' },
        { icon: MessageCircle, title: 'Real-time Q&A', description: 'Ask questions and get instant answers during live sessions' },
        { icon: Users, title: 'Peer Learning', description: 'Learn alongside motivated students in a collaborative environment' }
      ],
      'offline': [
        { icon: MapPin, title: 'Classroom Learning', description: 'Face-to-face learning in our state-of-the-art training centers in Indore' },
        { icon: Users, title: 'Direct Instructor Access', description: 'One-on-one interaction with mentors for personalized guidance' },
        { icon: Award, title: 'Hands-on Training', description: 'Practical learning with Instructor led lab facilities' },
        { icon: Building2, title: 'Professional Environment', description: 'Experience workplace-like environment with professional setup' },
        { icon: Coffee, title: 'Learning Resources', description: 'Get Hard copy Notes and Assignments for strong learning' },
        { icon: Briefcase, title: 'Placement Assistance', description: '100% placement assistance by our Placement Cell' }
      ],
      'self-paced': [
        { icon: Clock, title: 'Flexible Schedule', description: 'Learn at your own pace with 24/7 course access' },
        { icon: Headphones, title: 'Recorded Sessions', description: 'High-quality pre-recorded videos in Hindi' },
        { icon: Globe, title: 'Learn Anywhere', description: 'Access course content from any device, anytime, anywhere' },
        { icon: Award, title: 'Certification', description: 'Earn certificate by completing modules at your own speed' },
        { icon: FileText, title: 'Downloadable Resources', description: 'Access to study materials, Assignments, code, and project files' },
        { icon: MessageCircle, title: 'Community Support', description: 'Access to OSOPians Community and get support when needed' }
      ],
      'dac': [
       
        { icon: Building2, title: 'Corporate Training', description: 'Training designed for corporate requirements and industry needs' },
        { icon: Award, title: 'Real Time Doubt Solving', description: 'Get instant doubt resolution from expert mentors during live sessions' },
        { icon: Users, title: 'Expert Mentorship', description: 'Guidance from tech experts from industry'},
        { icon: MessageSquare, title: 'Interview Preparation', description: 'Comprehensive interview preparation with mock sessions' },
        { icon: FileTextIcon, title: 'Resume Building', description: 'Professional resume building and optimization guidance' },
        { icon: BrainIcon, title: 'Aptitude Preparation', description: 'Aptitude training for technical and logical reasoning' },
        { icon: UsersIcon, title: 'Mock Interviews', description: 'Regular mock interviews with industry experts' },
        { icon: Briefcase, title: 'Internship', description: 'Two internship opportunities with industry partners' },
        { icon: Code, title: 'Projects', description: 'Get hands on experience with 5 real projects' }
      ],
      'foundations': [
        { icon: BookOpen, title: 'Foundation Building', description: 'Strong programming fundamentals for complete beginners' },
        { icon: Code, title: 'Multiple Languages', description: 'Learn Python, JavaScript, HTML, CSS, and database concepts' },
        { icon: Target, title: 'Project-Based Learning', description: 'Build 5+ real-world projects to showcase your skills' },
        { icon: Users, title: 'Beginner-Friendly', description: 'No prior experience required - start from absolute basics' },
        { icon: Award, title: 'Foundation Certificate', description: 'Certificate validating your programming fundamentals' },
        { icon: Clock, title: '12-Week Program', description: 'Structured 12-week program with 3-4 hours per week' }
      ],
      'aiml': [
        { icon: Brain, title: 'AI & ML Specialization', description: 'Comprehensive AI and Machine Learning curriculum' },
        { icon: Bot, title: 'Deep Learning', description: 'Advanced neural networks and deep learning algorithms' },
        { icon: BarChart3, title: 'Data Science', description: 'Data analysis, visualization, and statistical modeling' },
        { icon: Cpu, title: 'Model Development', description: 'Build and deploy machine learning models' },
        { icon: Award, title: 'AI/ML Certification', description: 'Specialized certificate in Artificial Intelligence' },
        { icon: TrendingUp, title: 'Future-Ready Skills', description: 'Skills for the most in-demand AI/ML roles' }
      ],
      'free': [
        { icon: Gift, title: 'Free Access', description: 'High-quality courses completely free of cost' },
        { icon: Globe, title: 'Open Learning', description: 'Access to premium content without any payment' },
        { icon: Users, title: 'Community Learning', description: 'Learn with thousands of students worldwide' },
        { icon: BookOpen, title: 'Quality Education', description: 'Same quality content as paid courses, completely free' },
        { icon: Heart, title: 'Social Impact', description: 'Making quality education accessible to everyone' },
        { icon: Award, title: 'Free Certificate', description: 'Earn certificates without any hidden costs' }
      ]
    };
    
    return categoryFeatures[courseCategory] || [
    { icon: Globe, title: 'Online Learning', description: 'Learn from anywhere with our interactive online platform' },
    { icon: Award, title: 'Certification', description: 'Get industry-recognized certificate upon completion' },
    { icon: Users, title: 'Community', description: 'Join a community of learners and professionals' },
    { icon: Clock, title: 'Flexible Schedule', description: 'Learn at your own pace with recorded sessions' },
  ];
  };

  const reviews = [
    {
      name: "Shashwat Bajpai",
      rating: 5,
      review: "Amazing lecture sir. Far better than so called paid courses. Sir you are covering almost everything. This actually is what they call 'Quality Content'. Shocked with the number of views though",
      date: "1 month ago"
    },
    {
      name: "Ahmad Saleem",
      rating: 5,
      review: "Thanks alot Sir g. If greatness had a face it will definitely be like you 🥰🥰🥰",
      date: "3 weeks ago"
    },
    {
      name: "Blog.vestor",
      rating: 5,
      review: "Sir, your method of teaching is much better than others. Due to which we do not have any problem in understanding the concept.",
      date: "1 month ago"
    },
    {
      name: "Shivanshi Singh",
      rating: 5,
      review: "Thank you so much sir for providing us with such detailed concepts and making it easy to understand. I Understood everything from basic and feel happy to learn",
      date: "2 weeks ago"
    },
    {
      name: "Vyankatesh Birajdar",
      rating: 5,
      review: "sir i like your videos you cover all important topic in detail and guide how to prepare it for interview",
      date: "3 weeks ago"
    },
    {
      name: "Asiis",
      rating: 5,
      review: "coders boht achay hon gay youtube pe but this person is the best coding teacher. teaching is a different paradigm from coding.....",
      date: "1 month ago"
    },
    {
      name: "Manish Pant",
      rating: 5,
      review: "Never seen such a great video on exception handling. Thank a lot",
      date: "2 weeks ago"
    },
    {
      name: "Vinod Sharma",
      rating: 5,
      review: "So much value in this channel. I hope more people find and use it.",
      date: "3 weeks ago"
    },
    {
      name: "FatherOfAbrahamicReligio",
      rating: 5,
      review: "You are the God Of Java",
      date: "1 month ago"
    },
    {
      name: "FatherOfAbrahamicReligio",
      rating: 5,
      review: "Amazing lecture ❤️ sir, keep on doing your. You have a unique style of teaching that no one has.",
      date: "2 weeks ago"
    },
    {
      name: "Shiralijain9591",
      rating: 5,
      review: "Nested query explained in best possible way. Know the actual difference between joins and nesting. Amazing lecture sir 👌",
      date: "3 weeks ago"
    },
    {
      name: "Memo8435",
      rating: 5,
      review: "Amazing 😍😍",
      date: "1 month ago"
    },
    {
      name: "Pixelbihari",
      rating: 5,
      review: "bilkul sahi kaha sir aapne (You said it absolutely right, sir)",
      date: "2 weeks ago"
    },
    {
      name: "Filmreactionbyakk6909",
      rating: 5,
      review: "Thanks sir aapki bajah se coding me confidence aaya h (Thanks sir, because of you I gained confidence in coding)",
      date: "3 weeks ago"
    },
    {
      name: "Filmreactionbyakk6",
      rating: 5,
      review: "Functions ab mujhe ache se smjh me aata hai apki wajah se sir. Big respect to bajpai sir ❤️❤️",
      date: "1 month ago"
    },
    {
      name: "Rohitsinghkushwaha3",
      rating: 5,
      review: "Feeling very confident after making all programs on vs code with absolutely ZERO error. Thankyou Sir #CodingKranti@Bajpai Sir",
      date: "2 weeks ago"
    },
    {
      name: "Bhaweshjoshi6705",
      rating: 5,
      review: "Unbeatable king in the programming languages......thank you sir for making programming as a 'baccho ka khel' (child's play)",
      date: "3 weeks ago"
    },
    {
      name: "Rohitsinghkushwaha332",
      rating: 5,
      review: "Superb video for beginners ...Thanks Sir",
      date: "1 month ago"
    },
    {
      name: "SakshiSharma-xp9lt",
      rating: 5,
      review: "The lectures are so detailed even a student with a non IT background can understand.",
      date: "2 weeks ago"
    },
    {
      name: "Agnostic97",
      rating: 5,
      review: "mai pichhle 3 saal se coding ke basic sikhne ki koshish kr raha tha but concept clear nahi ho pa raha tha, aaj OSOP coding channel pe smjh aya. Itna basic and simple concept aaj tak kahi nahi dekha. Thanks a lot OSOP coding, u really made my day. 🙏🙏🙏😊😊😊",
      date: "3 weeks ago"
    },
    {
      name: "Bhavna2320",
      rating: 5,
      review: "If you are here.. Congratulations 🎉🎊🥳 you have amazing content of coding with great personality as bajpai sir 🙏🙏",
      date: "1 month ago"
    },
    {
      name: "Shashwatbajpai8989",
      rating: 5,
      review: "So good No one explained genric better than this on YouTube",
      date: "2 weeks ago"
    },
    {
      name: "Officaildiamondfact",
      rating: 5,
      review: "This is right platform for as a beginners",
      date: "3 weeks ago"
    }
  ];



    // Course-specific FAQs
    const getCourseSpecificFAQs = (courseCategory) => {
        const faqData = {
            'foundations': [
                {
                    question: 'Do I need any programming experience for this course?',
                    answer: 'No prior programming experience is required! This course is designed for complete beginners. We start from the very basics and build your foundation step by step.'
                },
                {
                    question: 'What programming languages will I learn?',
                    answer: 'You will learn Python and JavaScript fundamentals, along with HTML, CSS, and basic database concepts. These are the most in-demand skills for beginners.'
                },
                {
                    question: 'How long will it take to complete this course?',
                    answer: 'The course is designed for 12 weeks with 3-4 hours of study per week. However, you can learn at your own pace with lifetime access.'
                },
                {
                    question: 'Will I be able to build real projects?',
                    answer: 'Absolutely! You will build 5+ real-world projects including a personal portfolio website, calculator app, and a simple e-commerce site.'
                },
                {
                    question: 'What support will I get during the course?',
                    answer: 'You get 24/7 doubt support, live Q&A sessions, peer learning groups, and direct access to instructors for personalized guidance.'
                }
            ],
            'full-stack': [
                {
                    question: 'What technologies are covered in this full-stack course?',
                    answer: 'You will learn MERN stack (MongoDB, Express.js, React.js, Node.js), along with advanced concepts like authentication, APIs, deployment, and cloud services.'
                },
                {
                    question: 'Do I need to know programming basics before joining?',
                    answer: 'Yes, basic knowledge of HTML, CSS, and JavaScript is recommended. If you are a beginner, we suggest starting with our Foundations course first.'
                },
                {
                    question: 'How many projects will I build?',
                    answer: 'You will build 8+ full-stack projects including a social media app, e-commerce platform, real-time chat application, and a job portal.'
                },
                {
                    question: 'Will I learn deployment and hosting?',
                    answer: 'Yes! You will learn to deploy your applications on platforms like Heroku, Vercel, and AWS. We also cover CI/CD pipelines and production optimization.'
                },
                {
                    question: 'What kind of job roles can I apply for after this course?',
                    answer: 'You can apply for roles like Full Stack Developer, MERN Stack Developer, Frontend Developer, Backend Developer, and Web Application Developer.'
                }
            ],
            'data-science': [
                {
                    question: 'What is the difference between Data Science and Data Analytics?',
                    answer: 'Data Science focuses on building predictive models and machine learning algorithms, while Data Analytics focuses on analyzing historical data for insights. This course covers both aspects comprehensively.'
                },
                {
                    question: 'Do I need strong mathematics background?',
                    answer: 'Basic mathematics knowledge is sufficient. We teach all required concepts including statistics, probability, and linear algebra from scratch with practical examples.'
                },
                {
                    question: 'Which tools and technologies will I learn?',
                    answer: 'You will learn Python, Pandas, NumPy, Matplotlib, Scikit-learn, TensorFlow, SQL, and various data visualization tools. We also cover cloud platforms like AWS and Google Cloud.'
                },
                {
                    question: 'What kind of projects will I work on?',
                    answer: 'You will work on real-world projects like customer segmentation, sales prediction, sentiment analysis, image classification, and recommendation systems.'
                },
                {
                    question: 'Can I get a job in Data Science after this course?',
                    answer: 'Yes! Our placement record shows 85% of students get placed as Data Scientists, Data Analysts, or ML Engineers within 6 months of course completion.'
                }
            ],
            'aiml': [
                {
                    question: 'What is the difference between AI and Machine Learning?',
                    answer: 'AI is the broader concept of machines performing tasks that typically require human intelligence, while Machine Learning is a subset of AI that focuses on algorithms learning from data. This course covers both comprehensively.'
                },
                {
                    question: 'Do I need a computer science degree for AI/ML?',
                    answer: 'No! While a CS background helps, our course is designed for learners from all backgrounds. We teach everything from fundamentals to advanced concepts with practical applications.'
                },
                {
                    question: 'Which AI/ML frameworks will I learn?',
                    answer: 'You will learn TensorFlow, PyTorch, Scikit-learn, Keras, and various other frameworks. We also cover deep learning, neural networks, and natural language processing.'
                },
                {
                    question: 'What kind of AI projects will I build?',
                    answer: 'You will build projects like chatbots, image recognition systems, recommendation engines, autonomous vehicle simulation, and natural language processing applications.'
                },
                {
                    question: 'Is there a high demand for AI/ML professionals?',
                    answer: 'Absolutely! AI/ML is one of the fastest-growing fields with high salaries. Companies across all industries are actively hiring AI/ML engineers and researchers.'
                }
            ],
            'dac': [
                {
                    question: 'What is Data Analytics and why is it important?',
                    answer: 'Data Analytics involves examining data sets to draw conclusions and identify patterns. It helps businesses make informed decisions, optimize operations, and understand customer behavior.'
                },
                {
                    question: 'Which tools will I learn in this course?',
                    answer: 'You will learn Excel, SQL, Python, Tableau, Power BI, and various statistical analysis tools. We also cover data visualization and reporting techniques.'
                },
                {
                    question: 'Do I need programming experience for data analytics?',
                    answer: 'Basic programming knowledge is helpful but not mandatory. We teach Python and SQL from scratch, focusing on practical data analysis applications.'
                },
                {
                    question: 'What kind of analytics projects will I work on?',
                    answer: 'You will work on projects like sales analysis, customer behavior analysis, market research, financial analysis, and business intelligence dashboards.'
                },
                {
                    question: 'What career opportunities are available in Data Analytics?',
                    answer: 'You can work as a Data Analyst, Business Analyst, Market Research Analyst, Financial Analyst, or Analytics Consultant across various industries.'
                }
            ],
            'devops': [
                {
                    question: 'What is DevOps and why do companies need it?',
                    answer: 'DevOps is a set of practices that combines software development and IT operations to shorten development cycles and deliver high-quality software faster. Companies need it for better collaboration and faster deployments.'
                },
                {
                    question: 'Which tools and technologies will I learn?',
                    answer: 'You will learn Docker, Kubernetes, Jenkins, Git, AWS, Azure, Terraform, Ansible, and various monitoring and logging tools. We cover the complete DevOps toolchain.'
                },
                {
                    question: 'Do I need to be a developer to learn DevOps?',
                    answer: 'While development experience helps, it\'s not mandatory. We teach all concepts from basics, including Linux administration, scripting, and automation.'
                },
                {
                    question: 'What kind of DevOps projects will I work on?',
                    answer: 'You will work on projects like CI/CD pipeline setup, container orchestration, infrastructure automation, monitoring systems, and cloud migration strategies.'
                },
                {
                    question: 'What are the career prospects in DevOps?',
                    answer: 'DevOps engineers are in high demand with excellent salaries. You can work as a DevOps Engineer, Site Reliability Engineer, Cloud Engineer, or Platform Engineer.'
                }
            ]
        };

        return faqData[courseCategory] || faqData['foundations'];
    };

    // Get related courses based on current course category
    const getRelatedCourses = (currentCourse) => {
      if (!currentCourse) return [];
      
      const relatedCourses = [];
      const currentCategory = currentCourse.category;
      
      // Get courses from the same category (excluding current course)
      if (allCourses[currentCategory]) {
        const sameCategoryCourses = allCourses[currentCategory].filter(c => c.id !== currentCourse.id);
        relatedCourses.push(...sameCategoryCourses.slice(0, 2));
      }
      
      // Get courses from other popular categories
      const popularCategories = ['dac', 'live', 'self-paced'];
      for (const category of popularCategories) {
        if (category !== currentCategory && allCourses[category]) {
          const otherCategoryCourses = allCourses[category].slice(0, 1);
          relatedCourses.push(...otherCategoryCourses);
        }
      }
      
      // Return unique courses (max 4)
      return relatedCourses.slice(0, 4);
    };

    const faqs = getCourseSpecificFAQs(course?.category);
    const relatedCourses = getRelatedCourses(course);

    return (
      <>
        <VideoModal 
          videoUrl={course?.videoUrl}
          isOpen={isVideoOpen}
          onClose={() => setIsVideoOpen(false)}
        />

        {/* Video Testimonial Modal */}
        {videoModal.isOpen && (
          <div className="fixed inset-0 z-[10010] flex items-center justify-center p-4 bg-black/80">
            <div className="relative w-full max-w-4xl mx-auto">
              {/* Close button */}
              <button
                onClick={closeVideoModal}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
              >
                <X className="w-8 h-8" />
              </button>
              
              {/* Video container with 16:9 aspect ratio */}
              <div className="relative pt-[56.25%] w-full bg-black rounded-xl overflow-hidden">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src={videoModal.videoUrl}
                  title={`${videoModal.studentName} - ${videoModal.company} Testimonial`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                  allowFullScreen
                  onError={(e) => {
                    console.error('YouTube iframe error:', e);
                  }}
                ></iframe>
              </div>
              

            </div>
          </div>
        )}
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pt-20">
          {/* Hero Section - Simplified */}
          <section className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-6">
            <div className="container-max">
              {/* Breadcrumb */}
              <div className="text-primary-200 mb-2 text-xs">
                <Link to="/" className="hover:text-white">Home</Link> 
                <span className="mx-1">›</span>
                <Link to={`/courses/${course.category}`} className="hover:text-white capitalize">
                  {course.category.replace('-', ' ')}
                </Link>
                <span className="mx-1">›</span>
                <span className="text-white">{course.title}</span>
              </div>

              {/* Course Badge */}
              <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-3 py-1 rounded mb-2 text-xs font-semibold">
                <span className="capitalize">{course.category.replace('-', ' ')}</span>
                {course.isLive && <span className="ml-2 bg-green-500 text-white px-2 py-1 rounded-full text-[10px]">LIVE</span>}
              </div>

              {/* Course Title and Stats */}
              <div className="mb-8">
                <div className="flex flex-col mb-4">
                  <h1 className="text-3xl font-bold text-white mb-1">{course.banner?.title || course.title}</h1>
                  {course.banner?.subtitle && (
                    <h2 className="text-2xl font-bold text-white/90">{course.banner.subtitle}</h2>
                  )}
                </div>

              </div>
            </div>
          </section>

          {/* Main Content - Grouped Overview & Video/Enroll Card */}
          <div className="container-max py-8">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 flex flex-col lg:flex-row overflow-hidden">
              {/* Left: Overview */}
              <div className="flex-1 p-6 lg:p-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                  <BookOpen className="w-6 h-6 mr-3 text-primary-600" />
                  Course Overview
                </h2>
                
                <p className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-6">
                  {course.description}
                </p>
                {course.overview && (
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                    {course.overview}
                  </p>
                )}
                
                {/* Course Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-5 border border-blue-100 dark:border-blue-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">Duration</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.duration || '2.0 Hrs'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-5 border border-green-100 dark:border-green-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <PlayCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <span className="text-xs font-bold text-green-600 dark:text-green-400 uppercase tracking-wider">Lectures</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.lectures || '4'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-2xl p-5 border border-purple-100 dark:border-purple-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Monitor className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span className="text-xs font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">Mode</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.mode || 'Self Recorded'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-2xl p-5 border border-orange-100 dark:border-orange-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <MessageCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                      </div>
                      <span className="text-xs font-bold text-orange-600 dark:text-orange-400 uppercase tracking-wider">Language</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.language || 'Hindi/English'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-2xl p-5 border border-teal-100 dark:border-teal-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Target className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      </div>
                      <span className="text-xs font-bold text-teal-600 dark:text-teal-400 uppercase tracking-wider">Skill Level</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.level || 'Beginner'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-2xl p-5 border border-yellow-100 dark:border-yellow-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Award className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                      </div>
                      <span className="text-xs font-bold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">Certificate</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.certificate || 'Industry Recognized Certificate'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 rounded-2xl p-5 border border-pink-100 dark:border-pink-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-pink-100 dark:bg-pink-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <FileText className="w-5 h-5 text-pink-600 dark:text-pink-400" />
                      </div>
                      <span className="text-xs font-bold text-pink-600 dark:text-pink-400 uppercase tracking-wider">Tests</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.tests || '10'}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-5 border border-indigo-100 dark:border-indigo-800/30 hover:shadow-lg transition-all duration-300 group">
                    <div className="flex items-center mb-3">
                      <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/50 rounded-xl flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-200">
                        <Code className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      </div>
                      <span className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Assignments</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight">{course.assignments || '10'}</p>
                  </div>
                </div>
              </div>
              {/* Right: Video/Enroll */}
              <div className="w-full max-w-md lg:border-l border-gray-100 dark:border-gray-700 flex-shrink-0">
                <div className="sticky top-24">
                  {/* Video Preview */}
                  <div className="bg-white dark:bg-gray-800 rounded-none rounded-r-2xl shadow-none border-none overflow-hidden">
                    <div className="aspect-video bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
                      {thumbnail ? (
                        <img 
                          src={thumbnail} 
                          alt="Video thumbnail" 
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-black/20"></div>
                          <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
                        </>
                      )}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button 
                          onClick={handleVideoClick}
                          className={`w-16 h-16 ${course?.videoUrl ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30 cursor-pointer' : 'bg-gray-500/20 cursor-not-allowed'} rounded-full flex items-center justify-center transition-all duration-300 group hover:scale-110`}
                        >
                          <PlayCircle className={`w-8 h-8 ${course?.videoUrl ? 'text-white' : 'text-gray-400'} group-hover:scale-110 transition-transform duration-200`} />
                        </button>
                      </div>
                      <div className="absolute top-3 left-3">
                        <span className="bg-white/20 backdrop-blur-sm text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                          {course?.videoUrl ? 'Watch Preview' : 'Preview Coming Soon'}
                        </span>
                      </div>
                      <div className="absolute bottom-3 right-3">
                        <span className="bg-black/50 text-white px-2 py-0.5 rounded text-xs">
                          5:30
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      {/* Pricing */}
                      <div className="flex flex-col items-center justify-center mb-4">
                        <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">Now</span>
                        <span className="text-3xl font-extrabold text-Blue-600 drop-shadow-sm">₹{course.price}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xl text-gray-800 dark:text-gray-200 font-semibold uppercase">Course Fee</span>
                          <span className="text-2xl text-gray-800 dark:text-gray-200 line-through">₹{course.originalPrice}</span>
                          {course.originalPrice && (
                            <span className="ml-2 bg-green-300 text-green-800 px-2 py-0.5 rounded-full text-l font-bold shadow">
                              {Math.round((1 - course.price / course.originalPrice) * 100)}% OFF
                            </span>
                          )}
                        </div>
                      </div>
                      {/* Action Buttons */}
                      <div className="space-y-2 mb-4">
                        <button 
                          onClick={handleEnrollNow}
                          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 text-base hover:shadow-lg transform hover:scale-105"
                        >
                          🚀 Enroll Now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* The rest of the page remains unchanged (highlights, what you'll get, etc.) */}
            <div className="container-max py-12">
              <div className="grid lg:grid-cols-5 gap-12">
                {/* Left Side - Course Details (3/5 width) */}
                <div className="lg:col-span-3 space-y-8">
                  {/* Key Highlights */}
                  {course.highlights && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                        <Target className="w-6 h-6 mr-3 text-primary-600" />
                        Key Highlights
                      </h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        {course.highlights.map((highlight, index) => (
                          <div key={index} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 dark:text-gray-400">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* What You'll Learn */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Zap className="w-6 h-6 mr-3 text-primary-600" />
                      What You'll Get
                    </h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      {getCategoryFeatures(course.category).map((feature, index) => (
                        <div key={index} className="flex items-start p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-600">
                          <feature.icon className="w-6 h-6 text-primary-600 mr-4 mt-1 flex-shrink-0" />
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile Quick Actions, Reviews & Related Courses - Only visible on mobile */}
                  <div className="lg:hidden space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-6">
                      <div className="space-y-3 mb-6">
                        <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center">
                          <Download className="w-4 h-4 mr-2" />
                          Download Brochure
                        </button>
                        <button className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center">
                          <Share2 className="w-4 h-4 mr-2" />
                          Share Course
                        </button>
                      </div>

                      {/* Student Reviews */}
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                          <Star className="w-5 h-5 mr-2 text-primary-600" />
                          Student Reviews
                        </h3>
                        <div className="space-y-4">
                          {(showAllReviews ? reviews : reviews.slice(0, 5)).map((review, index) => (
                            <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-100 dark:border-gray-600">
                              <div className="flex items-center mb-3">
                                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-3">
                                  <User className="w-4 h-4 text-primary-600" />
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{review.name}</h4>
                                  <div className="flex items-center">
                                    {[...Array(5)].map((_, i) => (
                                      <Star 
                                        key={i} 
                                        className={`w-3 h-3 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                      />
                                    ))}
                                    <span className="ml-2 text-xs text-gray-500">{review.date}</span>
                                  </div>
                                </div>
                              </div>
                              <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3">{review.review}</p>
                            </div>
                          ))}
                        </div>
                        {reviews.length > 5 && (
                          <button
                            onClick={() => setShowAllReviews(!showAllReviews)}
                            className="w-full mt-4 text-primary-600 hover:text-primary-700 font-semibold text-sm py-2 px-4 rounded-lg border border-primary-200 hover:border-primary-300 transition-colors duration-200"
                          >
                            {showAllReviews ? 'Show Less' : 'View All Reviews'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Mobile Related Courses */}
                    {relatedCourses.length > 0 && (
                      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-6">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                          <BookOpen className="w-5 h-5 mr-2 text-primary-600" />
                          Related Courses
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Explore more courses to accelerate your career</p>
                        <div className="space-y-4">
                          {relatedCourses.map((relatedCourse, index) => (
                            <motion.div
                              key={relatedCourse.id}
                              initial={{ opacity: 0, y: 20 }}
                              whileInView={{ opacity: 1, y: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.5, delay: index * 0.1 }}
                              className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-600 overflow-hidden hover:shadow-md transition-shadow duration-200"
                            >
                              <Link to={`/course/${relatedCourse.slug || relatedCourse.id}`} className="block p-4">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-12 h-12 rounded-lg ${relatedCourse.banner.background} flex items-center justify-center flex-shrink-0`}>
                                    {relatedCourse.banner.Icon && (
                                      <relatedCourse.banner.Icon className="w-6 h-6 text-white" />
                                    )}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2 mb-1">
                                      {relatedCourse.title}
                                    </h4>
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center space-x-1">
                                        <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                        <span className="text-xs text-gray-500">{relatedCourse.rating}</span>
                                      </div>
                                      <span className="text-xs font-semibold text-primary-600">
                                        ₹{relatedCourse.price}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Course Curriculum */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                      <Code className="w-6 h-6 mr-3 text-primary-600" />
                      Course Curriculum
                    </h2>
                    <div className="space-y-4">
                      {course.curriculum?.map((week, index) => {
                        const open = openCurriculum[index];
                        return (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                            <button
                              onClick={() => setOpenCurriculum(curr => curr.map((v, i) => i === index ? !v : v))}
                              className="w-full flex justify-between items-center px-6 py-4 text-left text-lg font-semibold text-primary-700 dark:text-primary-300 focus:outline-none"
                            >
                              <span>{week.title}</span>
                              {open ? (
                                <ChevronUp className="w-5 h-5 text-primary-500" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </button>
                            <AnimatePresence initial={false}>
                              {open && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="overflow-hidden px-6 pb-4"
                                >
                                  {week.topics && (
                                    <ul className="space-y-2 mt-2">
                                      {week.topics.map((topic, tIndex) => (
                                        <li key={tIndex} className="flex items-start text-gray-600 dark:text-gray-300">
                                          <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                                          {topic}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>

                {/* Right Side - Reviews & Related Courses (2/5 width) - Only visible on desktop */}
                <div className="hidden lg:block lg:col-span-2 space-y-8">
                  {/* Quick Actions */}
                  <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-6">
                    <div className="space-y-3 mb-6">
                      <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center">
                        <Download className="w-4 h-4 mr-2" />
                        Download Brochure
                      </button>
                      <button className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white font-semibold py-3 px-4 rounded-xl transition-colors duration-200 flex items-center justify-center">
                        <Share2 className="w-4 h-4 mr-2" />
                        Share Course
                      </button>
                    </div>

                    {/* Student Reviews */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                        <Star className="w-5 h-5 mr-2 text-primary-600" />
                        Student Reviews
                      </h3>
                      <div className="space-y-4">
                        {(showAllReviews ? reviews : reviews.slice(0, 5)).map((review, index) => (
                          <div key={index} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-100 dark:border-gray-600">
                            <div className="flex items-center mb-3">
                              <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mr-3">
                                <User className="w-4 h-4 text-primary-600" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-gray-900 dark:text-white text-sm">{review.name}</h4>
                                <div className="flex items-center">
                                  {[...Array(5)].map((_, i) => (
                                    <Star 
                                      key={i} 
                                      className={`w-3 h-3 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                                    />
                                  ))}
                                  <span className="ml-2 text-xs text-gray-500">{review.date}</span>
                                </div>
                              </div>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-3">{review.review}</p>
                          </div>
                        ))}
                      </div>
                      {reviews.length > 5 && (
                        <button
                          onClick={() => setShowAllReviews(!showAllReviews)}
                          className="w-full mt-4 text-primary-600 hover:text-primary-700 font-semibold text-sm py-2 px-4 rounded-lg border border-primary-200 hover:border-primary-300 transition-colors duration-200"
                        >
                          {showAllReviews ? 'Show Less' : 'View All Reviews'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Related Courses */}
                  {relatedCourses.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                        <BookOpen className="w-5 h-5 mr-2 text-primary-600" />
                        Related Courses
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Explore more courses to accelerate your career</p>
                      <div className="space-y-4">
                        {relatedCourses.map((relatedCourse, index) => (
                          <motion.div
                            key={relatedCourse.id}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                            className="bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-100 dark:border-gray-600 overflow-hidden hover:shadow-md transition-shadow duration-200"
                          >
                            <Link to={`/course/${relatedCourse.slug || relatedCourse.id}`} className="block p-4">
                              <div className="flex items-center space-x-3">
                                <div className={`w-12 h-12 rounded-lg ${relatedCourse.banner.background} flex items-center justify-center flex-shrink-0`}>
                                  {relatedCourse.banner.Icon && (
                                    <relatedCourse.banner.Icon className="w-6 h-6 text-white" />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-2 mb-1">
                                    {relatedCourse.title}
                                  </h4>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-1">
                                      <Star className="w-3 h-3 text-yellow-400 fill-current" />
                                      <span className="text-xs text-gray-500">{relatedCourse.rating}</span>
                                    </div>
                                    <span className="text-xs font-semibold text-primary-600">
                                      ₹{relatedCourse.price}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Full Width Sections */}
              <div className="space-y-8 mt-12">
                {/* Meet Your Instructor */}
                  
      <MentorSection />


                {/* Success Stories */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <Trophy className="w-6 h-6 mr-3 text-primary-600" />
                   Our Alumni
                  </h2>
                  <SuccessStories />
                </div>

                {/* Real Feedback & Videos */}
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/10 dark:to-primary-800/10 py-20 rounded-3xl">
                  <motion.div 
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                     Success Stories
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                      How OSOPCoding Helped Students in Cracking their Dream IT MNC
                    </p>
                  </motion.div>

                  <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                      {videoTestimonials.slice(0, showAllVideos ? videoTestimonials.length : 4).map((testimonial, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group hover:-translate-y-2 h-full flex flex-col cursor-pointer"
                          onClick={() => openVideoModal(testimonial)}
                        >
                          <div className="relative aspect-video bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex-shrink-0 overflow-hidden">
                            {/* Video Thumbnail */}
                            {testimonial.thumbnail ? (
                              <img 
                                src={testimonial.thumbnail} 
                                alt={`${testimonial.name} testimonial thumbnail`}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  // Fallback to gradient background if thumbnail fails to load
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/20 dark:to-primary-800/20 flex items-center justify-center">
                                <div className="text-center">
                                  <div className="w-16 h-16 bg-primary-600/20 rounded-full flex items-center justify-center mb-2 mx-auto">
                                    <Play className="w-6 h-6 text-primary-600" />
                                  </div>
                                  <p className="text-sm text-primary-600 font-medium">Video Coming Soon</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Play Button Overlay */}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="w-16 h-16 bg-primary-600 hover:bg-primary-700 rounded-full flex items-center justify-center transition-all duration-200 group-hover:scale-110 shadow-2xl">
                                <Play className="w-6 h-6 text-white ml-1" />
                              </div>
                            </div>
                            
                            {/* Duration overlay */}
                            {testimonial.videoUrl !== "#" && (
                              <div className="absolute bottom-3 right-3 bg-black/80 text-white px-2 py-1 rounded text-xs font-medium">
                                Video
                              </div>
                            )}
                          </div>
                          <div className="p-4 flex-grow flex flex-col justify-between">
                            <div>
                              <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1 line-clamp-1">
                                {testimonial.name}
                              </h3>
                              <p className="text-primary-600 dark:text-primary-400 text-sm font-medium mb-3 line-clamp-1">
                                {testimonial.company}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Show All Button */}
                    {videoTestimonials.length > 4 && (
                      <motion.div 
                        className="text-center mt-12"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                      >
                        <button
                          onClick={() => setShowAllVideos(!showAllVideos)}
                          className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 mx-auto"
                        >
                          {showAllVideos ? (
                            <>
                              <span>Show Less</span>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                            </>
                          ) : (
                            <>
                              <span>Show All Videos</span>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </>
                          )}
                        </button>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Our Alumni are Placed in */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Our Alumni are Placed in</h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400">Top companies worldwide trust OSOP graduates</p>
                  </div>
                  <div className="w-full">
                    <img 
                      src={require('../assets/hiring-partners-grid.jpg')} 
                      alt="Hiring Partners Grid" 
                      className="w-full h-auto rounded-xl shadow-soft"
                    />
                  </div>
                </div>

                {/* Get Certificate Section */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                    {/* Left Side - Image */}
                    <motion.div
                      initial={{ opacity: 0, x: -50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                      className="relative"
                    >
                      <div className="relative bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl p-4 sm:p-6 lg:p-8 transform rotate-1 sm:rotate-2 hover:rotate-0 transition-transform duration-500">
                        {/* Rishi Vyas Data Analysis Image */}
                        <div className="relative overflow-hidden rounded-xl sm:rounded-2xl cursor-pointer group" onClick={openCertificateModal}>
                          <img 
                            src={rishiVyasImage} 
                            alt="Rishi Vyas Data Analysis"
                            className="w-full h-auto object-cover rounded-xl sm:rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105"
                          />
                          {/* Zoom overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 rounded-xl sm:rounded-2xl flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Decorative Elements */}
                      <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full animate-bounce"></div>
                      <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-blue-400 rounded-full animate-pulse"></div>
                      <div className="absolute top-1/2 -right-6 w-4 h-4 bg-green-400 rounded-full animate-ping"></div>
                    </motion.div>

                    {/* Right Side - Content */}
                    <motion.div
                      initial={{ opacity: 0, x: 50 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="space-y-6"
                    >
                      <div>
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-4 py-2 rounded-full text-sm font-medium">
                          🏆 Get Certified 
                        </span>
                      </div>

                      <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white leading-tight">
                        OSOP Certified IT Professional
                      </h2>

                      <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed">
                        Get industry recognized Certificate on completion of courses. Certified from OSOP Means the "Guarantee of Skills".
                      </p>

                      {/* OSOP Coding Certificate Features */}
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 shadow-lg border border-blue-200 dark:border-blue-800">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                          <Award className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-2" />
                          OSOP Coding Certificates
                        </h3>
                        <div className="space-y-4">
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Industry-Recognized</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Certificates recognized by IT companies worldwide</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Project-Based Learning</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Real projects included with every certification course</p>
                            </div>
                          </div>
                          <div className="flex items-start space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                              <CheckCircle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">26+ Years Excellence</h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400">Proven track record of 15K+ IT Professional trained in classroom</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* CTA Button */}
                      <div className="pt-4">
                        <Link 
                          to="/enquire"
                          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-2xl transition-all duration-300 inline-flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          <span>Get Certified Today</span>
                          <ArrowRight className="w-5 h-5" />
                        </Link>
                      </div>
                    </motion.div>
                  </div>
                </div>

                {/* FAQ */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft border border-gray-100 dark:border-gray-700 p-8">
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                    <MessageCircle className="w-6 h-6 mr-3 text-primary-600" />
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-4">
                    {faqs.map((faq, index) => (
                      <FAQItem key={index} {...faq} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certificate Modal */}
        <CertificateModal
          isOpen={certificateModal.isOpen}
          onClose={closeCertificateModal}
          imageSrc={rishiVyasImage}
          imageAlt="Rishi Vyas Data Analysis Certificate"
        />

      </>
    );
};

export default CourseDetail; 